import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';

import { VIEW_REFERENCES_CDM_SOLUTION_MODEL } from '../../../../constants/cdm-infield-data-model';
import { getElapsedTimeMs } from '../../../../utils/get-elapsed-time-ms';
import { QueryStatus } from '../../../../utils/query-status';
import { QueryKeys } from '../../../../utils/queryKeys';
import { METRICS_NAMESPACES } from '../../../metrics/constants';
import type { InFieldCDMLocationConfig } from '../../cdm-types';

import { UseGetAllInfieldLocationsContext } from './cdm-use-get-all-infield-locations.context';

export const useGetAllInfieldLocations = ({ enabled = true } = {}) => {
  const {
    useAuthContext,
    createInfieldAppConfigService,
    captureException,
    useInFieldMetrics,
  } = useContext(UseGetAllInfieldLocationsContext);
  const { client } = useAuthContext();
  const sliMetrics = useInFieldMetrics(
    QueryKeys.LOCATIONS_LIST,
    METRICS_NAMESPACES.SLI
  );

  const { data, isLoading, isError } = useQuery<InFieldCDMLocationConfig[]>({
    queryKey: [QueryKeys.LOCATIONS_LIST],
    enabled,
    queryFn: async () => {
      const sliTimerStartTime = Date.now();

      try {
        const infieldConfigService = createInfieldAppConfigService(
          client,
          VIEW_REFERENCES_CDM_SOLUTION_MODEL.infieldLocationConfig
        );

        const result = await infieldConfigService.listAll();

        const sliElapsedTimeMs = getElapsedTimeMs(sliTimerStartTime);
        sliMetrics.track(sliElapsedTimeMs, QueryStatus.SUCCESS);

        return result;
      } catch (error) {
        const sliElapsedTimeMs = getElapsedTimeMs(sliTimerStartTime);
        sliMetrics.track(sliElapsedTimeMs, QueryStatus.ERROR);
        captureException(error, {
          level: 'error',
          tags: {
            queryKey: QueryKeys.LOCATIONS_LIST,
          },
        });
        throw error;
      }
    },
  });

  return { data, isLoading, isError };
};
